import { useEffect, useState } from "react";
import type { OrderDto } from "@/shared/api/types";
import type { useBillingSummaryQuery } from "@/shared/hooks/useBillingSummary";
import { staleRenewalOf, type StaleRenewal } from "@/shared/utils/stale-renewal";

/** A stale renewal invoice, and whether the page should still be waiting for its replacement. */
export interface StaleRenewalState extends StaleRenewal {
  /**
   * True from the moment the invoice is found stale until the bounded polling window for its replacement
   * has run out. Pay is withheld only while this is true: if the worker could not replace the invoice
   * (the customer had just paid it, say) the window ends and the customer can pay what is in front of
   * them. A page that disables Pay for as long as `orderCurrent` is `false` could lock someone out of
   * paying for good, which is the one thing this must never do.
   */
  updating: boolean;
}

type BillingWatch = Pick<ReturnType<typeof useBillingSummaryQuery>, "data" | "watching" | "watchForChange">;

/**
 * The Subscription page's answer to a renewal invoice raised before the price changed (ADR-0015 addendum
 * of 2026-10-04). The api tells the portal the invoice is stale; the worker replaces it within seconds, so
 * the page opens the bounded polling window (`watchForChange`) ONCE per stale episode and picks up the
 * replacement by itself rather than asking the customer to refresh.
 *
 * An episode is a run of reads in which the same invoice is stale: it ends when the replacement lands
 * (the flag goes `true`/`null`) and a second change after that is a new episode with its own window. The
 * window is not reopened because the invoice is STILL stale when the first one closes — polling forever
 * for something that is not coming would be the interval left running that `useBillingSummaryQuery` is
 * built to avoid.
 *
 * Until the effect has opened the window, `updating` already reads true, so Pay does not flash enabled
 * for one frame between the read landing and the window starting.
 */
export function useStaleRenewal(billing: BillingWatch): StaleRenewalState | null {
  const stale = staleRenewalOf(billing.data);
  const orderId = stale?.orderId ?? null;
  const { watching, watchForChange } = billing;
  const [watchedFor, setWatchedFor] = useState<string | null>(null);

  useEffect(() => {
    if (orderId === null) {
      setWatchedFor(null);
      return;
    }
    if (watchedFor === orderId) return;
    setWatchedFor(orderId);
    watchForChange();
  }, [orderId, watchedFor, watchForChange]);

  if (!stale) return null;
  return { ...stale, updating: watching || watchedFor !== stale.orderId };
}

/**
 * What an `OrderDialog` should be told about the invoice it is showing: `replacing` while it is the one
 * being replaced and the window is open, nothing otherwise. Once the window has ended the dialog is left
 * to let the customer pay, as the page does.
 */
export function replacingOf(
  order: Pick<OrderDto, "id"> | null,
  stale: StaleRenewalState | null,
): { totalPaise: number } | null {
  if (!order || !stale?.updating || stale.orderId !== order.id || stale.totalPaise === null) return null;
  return { totalPaise: stale.totalPaise };
}
